import { Component } from '@theme/component';

/** The Yotpo instance that renders the PDP Reviews tab (custom liquid in templates/product*.json). */
const WIDGET_SELECTOR = '[data-yotpo-instance-id="506687"]';

/**
 * Where the summary goes inside the Reviews tab, in order of preference: in place of Yotpo's own
 * "Customers say", else above the media gallery, else above the review list. Tried one by one —
 * a combined selector would return whichever comes first in the document, not the preferred one.
 */
const ANCHOR_SELECTORS = ['.yotpo-paragraph-summary-container', '.yotpo-media-gallery', '.yotpo-reviews-container'];

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [questionsButton] - "See all questions", present when the block has Q&A.
 */

/**
 * Places the server-rendered review summary (snippets/sl-reviews-ssr.liquid) inside the Yotpo
 * Reviews tab once the widget has rendered, so the page shows ONE "Customers say" in the position
 * shoppers expect, instead of Yotpo's copy at the top and ours below the pagination.
 *
 * Everything here is additive. The block is complete and visible where Liquid printed it, which is
 * what a crawler and a shopper with Yotpo blocked get. Moving it only changes where it sits; its
 * text never leaves the page and is never hidden (SEO rules in the snippet's doc).
 *
 * Moving a node into Yotpo's tree is safe here: measured on theme dev 2026-09-14, Yotpo keeps the
 * same header, summary and panel nodes through pagination, the media and star filters, and switching
 * between the Reviews and Q&A tabs, and a foreign node placed beside the summary survived all of it.
 *
 * @extends {Component<Refs>}
 */
export class ReviewsSsrComponent extends Component {
  /** @type {Element | null} Where Liquid printed the block; it also contains the widget. */
  #home = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#home ??= this.parentElement;

    // Watched for as long as the block is on the page, not just until the first placement: Yotpo
    // renders its "Customers say" AFTER the rest of the panel (the AI summary is a separate
    // request), so the block can land above the media gallery first and must move up once that
    // summary appears. Measured 2026-09-14 — it came late on the F30i desktop and the AirSense 11
    // mobile, early on the others. #place() only moves the node when it is out of position, so the
    // mutation its own move causes settles on the next call.
    this.#place();
    this.#watch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unwatch();
  }

  /** Opens Yotpo's Q&A tab. Bound with `on:click="/openQuestions"`. */
  openQuestions() {
    const tab = this.#questionsTab();
    if (!tab) return;

    tab.click();
    tab.focus({ preventScroll: true });
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    tab.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  /** @returns {boolean} True when the block sits inside the Reviews tab. */
  #place() {
    const panel = this.#widget()?.querySelector('.yotpo-main-reviews-widget');
    if (!panel) return false;

    let anchor = null;
    for (const selector of ANCHOR_SELECTORS) {
      anchor = panel.querySelector(selector);
      if (anchor) break;
    }
    if (!anchor?.parentElement) return false;

    if (this.nextElementSibling !== anchor) anchor.parentElement.insertBefore(this, anchor);
    if (!this.hasAttribute('data-in-widget')) this.toggleAttribute('data-in-widget', true);
    this.#syncQuestionsButton();
    return true;
  }

  #watch() {
    if (this.#observer || !this.#home) return;
    this.#observer = new MutationObserver(() => this.#place());
    this.#observer.observe(this.#home, { childList: true, subtree: true });
  }

  #unwatch() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** The button only shows when there is a tab to open, so it can never be a dead click. */
  #syncQuestionsButton() {
    const button = this.refs.questionsButton;
    const hidden = !this.#questionsTab();
    if (button && button.hidden !== hidden) button.hidden = hidden;
  }

  /** @returns {HTMLElement | null} */
  #widget() {
    return this.#home?.querySelector(WIDGET_SELECTOR) ?? null;
  }

  /** @returns {HTMLElement | null} */
  #questionsTab() {
    const widget = this.#widget();
    if (!widget) return null;

    const byId = widget.querySelector('#yotpo-tabs-main-widget-tab-1');
    if (byId instanceof HTMLElement) return byId;

    const tabs = /** @type {HTMLElement[]} */ ([...widget.querySelectorAll('[role="tab"]')]);
    return tabs.find((tab) => /q\s*&\s*a|question/i.test(`${tab.textContent} ${tab.getAttribute('aria-label') ?? ''}`)) ?? null;
  }
}

if (!customElements.get('reviews-ssr-component')) {
  customElements.define('reviews-ssr-component', ReviewsSsrComponent);
}
